import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CatchingPokemonIcon from '@mui/icons-material/CatchingPokemon';
import CloseIcon from '@mui/icons-material/Close';
import usePokemonList from '../hooks/usePokemonList';
import SEO from '../components/SEO';
import { getPokemonDetailPath } from '../utils/navigationHelper';
import useDraggable from '../hooks/useDraggable';
import { readCache, writeCache } from '../utils/cacheStorage';

const RECORDS_STORAGE_KEY = 'higher-or-lower-records-v1';
const IMAGE_CACHE_STORAGE_KEY = 'higher-or-lower-image-cache-v2';
const IMAGE_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_PERSISTED_IMAGE_CACHE_ENTRIES = 400;
const RAW_GITHUB_PREFIX = 'https://raw.githubusercontent.com/PokeAPI/sprites/master/';
const JSDELIVR_GITHUB_MIRROR_PREFIX = 'https://cdn.jsdelivr.net/gh/PokeAPI/sprites@master/';
const INLINE_PLACEHOLDER_IMAGE = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240"><rect width="240" height="240" fill="%2317181d"/><circle cx="120" cy="120" r="72" fill="none" stroke="%2357637a" stroke-width="10"/><line x1="48" y1="120" x2="192" y2="120" stroke="%2357637a" stroke-width="10"/><circle cx="120" cy="120" r="18" fill="%2357637a"/><text x="120" y="215" text-anchor="middle" fill="%238ea0b7" font-family="Arial,sans-serif" font-size="18">No image</text></svg>';

const STAT_MODES = [
  { key: 'total', label: 'Total Stats' },
  { key: 'hp', label: 'HP' },
  { key: 'attack', label: 'Attack' },
  { key: 'defense', label: 'Defense' },
  { key: 'special-attack', label: 'Sp. Attack' },
  { key: 'special-defense', label: 'Sp. Defense' },
  { key: 'speed', label: 'Speed' }
];

const EMPTY_TYPES = [];

const initialRecords = STAT_MODES.reduce((acc, mode) => {
  acc[mode.key] = 0;
  return acc;
}, {});

const typeColorMap = {
  normal: '#A8A878',
  fire: '#F08030',
  water: '#6890F0',
  electric: '#F8D030',
  grass: '#78C850',
  ice: '#98D8D8',
  fighting: '#C03028',
  poison: '#A040A0',
  ground: '#E0C068',
  flying: '#A890F0',
  psychic: '#F85888',
  bug: '#A8B820',
  rock: '#B8A038',
  ghost: '#705898',
  dragon: '#7038F8',
  dark: '#705848',
  steel: '#B8B8D0',
  fairy: '#EE99AC'
};

const getStatValue = (pokemon, mode) => {
  const stats = pokemon?.stats || [];
  const getBase = (name) => stats.find((s) => s.name === name)?.baseStat || 0;

  if (mode === 'total') {
    return ['hp', 'attack', 'defense', 'special-attack', 'special-defense', 'speed']
      .map(getBase)
      .reduce((sum, value) => sum + value, 0);
  }

  return getBase(mode);
};

const loadRecords = () => {
  try {
    const raw = localStorage.getItem(RECORDS_STORAGE_KEY);
    if (!raw) return initialRecords;

    const parsed = JSON.parse(raw);
    return {
      ...initialRecords,
      ...parsed
    };
  } catch (error) {
    console.warn('Could not parse Higher or Lower records:', error);
    return initialRecords;
  }
};

const formatType = (typeName) => {
  if (!typeName) return 'Unknown';
  return typeName.charAt(0).toUpperCase() + typeName.slice(1);
};

const showdownSpecialNames = {
  'Urshifu-Rapid-Strike': 'urshifu-rapidstrike',
  'urshifu-rapid-strike': 'urshifu-rapidstrike',
  'Chien-Pao': 'chienpao',
  'chien-pao': 'chienpao',
  'Chi-Yu': 'chiyu',
  'chi-yu': 'chiyu',
  'Ting-Lu': 'tinglu',
  'ting-lu': 'tinglu',
  'Wo-Chien': 'wochien',
  'wo-chien': 'wochien',
  'Ho-Oh': 'hooh',
  'Necrozma-Dawn-Wings': 'necrozma-dawnwings',
  'Necrozma-Dusk-Mane': 'necrozma-duskmane',
  'Toxtricity-Low-Key': 'toxtricity-lowkey',
  'Kommo-o': 'kommoo',
  'Porygon-Z': 'porygonz',
  'Tauros-Paldea-Blaze': 'tauros-paldeablaze',
  'Tauros-Paldea-Aqua': 'tauros-paldeaaqua',
  'Tauros-Paldea-Combat': 'tauros-paldeacombat'
};

const toJsDelivrMirror = (url) => {
  if (!url || typeof url !== 'string') return null;
  if (!url.startsWith(RAW_GITHUB_PREFIX)) return null;
  return url.replace(RAW_GITHUB_PREFIX, JSDELIVR_GITHUB_MIRROR_PREFIX);
};

const getShowdownDexSpriteUrl = (pokemon) => {
  const name = pokemon?.name;
  if (!name) return null;

  const formattedName = showdownSpecialNames[name]
    || name.toLowerCase()
      .replace(/\s+/g, '')
      .replace(/[^a-z0-9-]/g, '');

  return `https://play.pokemonshowdown.com/sprites/dex/${formattedName}.png`;
};

const PokemonCandidateCard = ({ pokemon, imageSrc, onImageError, onPick, modeLabel, disabled, feedback }) => {
  if (!pokemon) return null;

  const image = imageSrc || pokemon.officialArtwork || pokemon.sprite || INLINE_PLACEHOLDER_IMAGE;
  const isWinner = feedback?.outcome === 'winner';
  const isTie = feedback?.outcome === 'tie';
  const isMemory = feedback?.outcome === 'memory';

  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 3,
        backgroundColor: 'rgba(30, 30, 30, 0.95)',
        border: feedback
          ? isMemory
            ? '1px solid rgba(100, 181, 246, 0.85)'
            : isWinner || isTie
            ? '1px solid rgba(56, 220, 150, 0.8)'
            : '1px solid rgba(255, 99, 99, 0.85)'
          : '1px solid rgba(255,255,255,0.08)',
        boxShadow: feedback
          ? isMemory
            ? '0 0 0 2px rgba(100,181,246,0.15), 0 10px 24px rgba(100,181,246,0.2)'
            : isWinner || isTie
            ? '0 0 0 2px rgba(56,220,150,0.15), 0 10px 24px rgba(56,220,150,0.2)'
            : '0 0 0 2px rgba(255,99,99,0.15), 0 10px 24px rgba(255,99,99,0.18)'
          : 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100%'
      }}
    >
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1 }}>
        #{String(pokemon.id || 0).padStart(4, '0')}
      </Typography>

      <Box
        component="img"
        src={image}
        alt={pokemon.displayName}
        onError={(e) => {
          const failedSrc = e.currentTarget.currentSrc || e.currentTarget.src;
          if (failedSrc === INLINE_PLACEHOLDER_IMAGE) {
            e.currentTarget.onerror = null;
            return;
          }
          onImageError?.(failedSrc);
          e.currentTarget.src = INLINE_PLACEHOLDER_IMAGE;
        }}
        sx={{
          width: 180,
          maxWidth: '100%',
          aspectRatio: '1 / 1',
          objectFit: 'contain',
          mb: 1.5
        }}
      />

      <Typography
        variant="h5"
        align="center"
        sx={{ color: 'white', fontWeight: 'bold', mb: 1.5, minHeight: 64, display: 'flex', alignItems: 'center' }}
      >
        {pokemon.displayName}
      </Typography>

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="center" sx={{ mb: 2 }}>
        {(pokemon.types || []).map((typeObj, index) => {
          const typeName = (typeObj?.name || typeObj?.type?.name || '').toLowerCase();
          return (
            <Chip
              key={`${pokemon.name}-${typeName}-${index}`}
              label={formatType(typeName)}
              size="small"
              sx={{
                backgroundColor: typeColorMap[typeName] || '#777',
                color: 'white',
                fontWeight: 'bold'
              }}
            />
          );
        })}
      </Stack>

      {feedback && (
        <Paper
          sx={{
            width: '100%',
            mb: 1.2,
            p: 1.2,
            borderRadius: 2,
            backgroundColor: isMemory
              ? 'rgba(100,181,246,0.12)'
              : isWinner || isTie
                ? 'rgba(36,204,159,0.12)'
                : 'rgba(255,99,99,0.12)',
            border: `1px solid ${isMemory
              ? 'rgba(100,181,246,0.4)'
              : isWinner || isTie
                ? 'rgba(36,204,159,0.4)'
                : 'rgba(255,99,99,0.4)'
            }`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 700 }}>
              {modeLabel}: <span style={{ color: '#fff', fontSize: '1.05rem' }}>{feedback.value}</span>
            </Typography>
            <Tooltip title="Open Pokémon details" arrow>
              <IconButton
                aria-label={`Open ${pokemon.displayName} details`}
                size="small"
                onClick={() => {
                  const detailPath = getPokemonDetailPath(pokemon);
                  if (!detailPath) return;
                  window.open(detailPath, '_blank', 'noopener,noreferrer');
                }}
                sx={{
                  color: '#ef5350',
                  border: '1px solid rgba(255,255,255,0.18)',
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(239,83,80,0.16)'
                  }
                }}
              >
                <CatchingPokemonIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>
      )}

      <Button
        onClick={onPick}
        variant="contained"
        fullWidth
        disabled={disabled}
        aria-label={`Choose ${pokemon.displayName} as having the highest ${modeLabel}`}
        sx={{
          mt: 'auto',
          py: 1.2,
          fontWeight: 'bold',
          borderRadius: 2,
          background: 'linear-gradient(90deg, #221FC7 0%, #24CC9F 100%)'
        }}
      >
        Choose this one
      </Button>
    </Paper>
  );
};

function DraggablePaperComponent(props) {
  const { ref, style, handleMouseDown } = useDraggable({
    resetOnClose: true,
    handleSelector: '#higher-lower-records-title'
  });

  return (
    <Paper
      {...props}
      ref={ref}
      style={{ ...props.style, ...style }}
      onMouseDown={handleMouseDown}
    />
  );
}

const HigherOrLowerPage = () => {
  const [mode, setMode] = useState('total');
  const [includeLegendaries, setIncludeLegendaries] = useState(true);
  const [onlyFinalEvolutions, setOnlyFinalEvolutions] = useState(false);
  const [includeMegas, setIncludeMegas] = useState(true);
  const [includeGigantamax, setIncludeGigantamax] = useState(true);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [roundFeedback, setRoundFeedback] = useState(null);
  const [carryOverFeedback, setCarryOverFeedback] = useState(null);
  const [isResolvingRound, setIsResolvingRound] = useState(false);
  const [currentPair, setCurrentPair] = useState({ left: null, right: null });
  const [carriedPokemonName, setCarriedPokemonName] = useState(null);
  const [recordsOpen, setRecordsOpen] = useState(false);
  const [records, setRecords] = useState(loadRecords);
  const [pairImageUrls, setPairImageUrls] = useState({ left: null, right: null });
  const gameSessionRef = useRef(0);
  const roundAdvanceTimeoutRef = useRef(null);
  const imageUrlCacheRef = useRef(
    new Map(Object.entries(readCache(IMAGE_CACHE_STORAGE_KEY, IMAGE_CACHE_TTL_MS) || {}))
  );
  // Limpiar clave v1 heredada para liberar espacio en localStorage
  localStorage.removeItem('higher-or-lower-image-cache-v1');
  const imageUrlFailuresRef = useRef(new Map());
  const imageCachePersistenceDisabledRef = useRef(false);

  const { pokemonList, loading, error } = usePokemonList({
    limit: 5000,
    types: EMPTY_TYPES,
    includeStats: true
  });

  const pokemonPool = useMemo(() => {
    return (pokemonList || []).filter((pokemon) => Array.isArray(pokemon.stats) && pokemon.stats.length > 0);
  }, [pokemonList]);

  const persistImageCache = () => {
    if (imageCachePersistenceDisabledRef.current) return;

    const persistableEntries = Array.from(imageUrlCacheRef.current.entries())
      .filter(([, url]) => {
        if (!url || url === '/placeholder-pokemon.png' || url === INLINE_PLACEHOLDER_IMAGE) {
          return false;
        }
        return !String(url).startsWith('data:');
      })
      .slice(-MAX_PERSISTED_IMAGE_CACHE_ENTRIES);

    const didPersist = writeCache(IMAGE_CACHE_STORAGE_KEY, Object.fromEntries(persistableEntries));
    if (!didPersist) {
      imageCachePersistenceDisabledRef.current = true;
      console.warn('Higher or Lower image cache persistence disabled for this session (localStorage quota exceeded).');
    }
  };

  const clearRoundAdvanceTimeout = () => {
    if (roundAdvanceTimeoutRef.current) {
      clearTimeout(roundAdvanceTimeoutRef.current);
      roundAdvanceTimeoutRef.current = null;
    }
  };

  const getSpriteProxyUrl = (pokemon) => {
    const identifier = pokemon?.name || pokemon?.id;
    if (!identifier) return null;
    return `/api/sprites/${encodeURIComponent(identifier)}`;
  };

  const getImageCandidates = (pokemon) => {
    const officialArtwork = pokemon?.officialArtwork || null;
    const officialArtworkMirror = toJsDelivrMirror(officialArtwork);
    const showdownFallback = getShowdownDexSpriteUrl(pokemon);

    const candidates = [
      officialArtworkMirror,
      officialArtwork,
      showdownFallback,
      getSpriteProxyUrl(pokemon),
      pokemon?.sprite,
      INLINE_PLACEHOLDER_IMAGE
    ];
    return Array.from(new Set(candidates.filter(Boolean)));
  };

  const resolvePokemonImageUrl = (pokemon) => {
    const cacheKey = pokemon?.name;
    if (!cacheKey) return INLINE_PLACEHOLDER_IMAGE;

    if (imageUrlCacheRef.current.has(cacheKey)) {
      return imageUrlCacheRef.current.get(cacheKey);
    }

    const failedCandidates = imageUrlFailuresRef.current.get(cacheKey) || new Set();
    const selectedCandidate = getImageCandidates(pokemon)
      .find((candidate) => !failedCandidates.has(candidate)) || INLINE_PLACEHOLDER_IMAGE;

    const previousCandidate = imageUrlCacheRef.current.get(cacheKey);
    if (previousCandidate !== selectedCandidate) {
      imageUrlCacheRef.current.set(cacheKey, selectedCandidate);
      persistImageCache();
    }
    return selectedCandidate;
  };

  const handleCardImageError = (side, failedSrc) => {
    const pokemon = side === 'left' ? currentPair.left : currentPair.right;
    const cacheKey = pokemon?.name;
    if (!cacheKey) return;

    const failedSet = imageUrlFailuresRef.current.get(cacheKey) || new Set();
    failedSet.add(failedSrc);
    imageUrlFailuresRef.current.set(cacheKey, failedSet);

    const nextCandidate = getImageCandidates(pokemon)
      .find((candidate) => !failedSet.has(candidate)) || INLINE_PLACEHOLDER_IMAGE;

    const previousCandidate = imageUrlCacheRef.current.get(cacheKey);
    if (previousCandidate !== nextCandidate) {
      imageUrlCacheRef.current.set(cacheKey, nextCandidate);
      persistImageCache();
    }

    setPairImageUrls((prev) => ({
      ...prev,
      [side]: nextCandidate
    }));
  };

  const passesActiveFilters = (pokemon) => {
    const pokemonSlug = (pokemon?.name || '').toLowerCase();

    if (!includeMegas && pokemonSlug.includes('-mega')) return false;
    if (!includeGigantamax && (pokemonSlug.includes('-gmax') || pokemonSlug.includes('-gigantamax'))) return false;
    if (!includeLegendaries && (pokemon.isLegendary || pokemon.isMythical)) return false;
    if (onlyFinalEvolutions && !pokemon.isFinalEvolution) return false;

    return true;
  };

  const pickRandomPokemon = (excludedNames = []) => {
    const exclusionSet = new Set(
      Array.isArray(excludedNames) ? excludedNames.filter(Boolean) : [excludedNames].filter(Boolean)
    );
    const filtered = pokemonPool.filter((p) => !exclusionSet.has(p.name) && passesActiveFilters(p));
    if (filtered.length === 0) return null;
    return filtered[Math.floor(Math.random() * filtered.length)];
  };

  const resetGame = async () => {
    const sessionId = gameSessionRef.current + 1;
    gameSessionRef.current = sessionId;
    clearRoundAdvanceTimeout();

    if (pokemonPool.length < 2) {
      if (sessionId !== gameSessionRef.current) return;
      setCurrentPair({ left: null, right: null });
      setScore(0);
      setGameOver(false);
      setRoundFeedback(null);
      setCarryOverFeedback(null);
      setIsResolvingRound(false);
      return;
    }

    const first = pickRandomPokemon();
    const second = pickRandomPokemon([first?.name]);

  if (sessionId !== gameSessionRef.current) return;

    setCurrentPair({ left: first, right: second });
    setScore(0);
    setGameOver(false);
    setRoundFeedback(null);
    setCarryOverFeedback(null);
    setCarriedPokemonName(null);
    setPairImageUrls({ left: null, right: null });
    setIsResolvingRound(false);
  };

  const saveRecordIfNeeded = (modeKey, finalScore) => {
    setRecords((prev) => {
      const best = prev[modeKey] || 0;
      if (finalScore <= best) return prev;

      const updated = {
        ...prev,
        [modeKey]: finalScore
      };

      localStorage.setItem(RECORDS_STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  useEffect(() => {
    if (pokemonPool.length >= 2) {
      void resetGame();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, includeLegendaries, onlyFinalEvolutions, includeMegas, includeGigantamax, pokemonPool.length]);

  useEffect(() => {
    const leftPokemon = currentPair.left;
    const rightPokemon = currentPair.right;

    if (!leftPokemon && !rightPokemon) {
      setPairImageUrls({ left: null, right: null });
      return;
    }

    setPairImageUrls({
      left: leftPokemon ? resolvePokemonImageUrl(leftPokemon) : null,
      right: rightPokemon ? resolvePokemonImageUrl(rightPokemon) : null
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPair.left, currentPair.right]);

  const handlePick = (pickedSide) => {
    if (gameOver || isResolvingRound || !currentPair.left || !currentPair.right) return;

    setIsResolvingRound(true);

    const pickedPokemon = pickedSide === 'left' ? currentPair.left : currentPair.right;
    const otherPokemon = pickedSide === 'left' ? currentPair.right : currentPair.left;
    const leftValue = getStatValue(currentPair.left, mode);
    const rightValue = getStatValue(currentPair.right, mode);

    const pickedValue = getStatValue(pickedPokemon, mode);
    const otherValue = getStatValue(otherPokemon, mode);
    const isCorrect = pickedValue >= otherValue;
    const isTie = leftValue === rightValue;
    const leftIsWinner = isTie || leftValue > rightValue;

    setRoundFeedback({
      pickedSide,
      isCorrect,
      isTie,
      leftValue,
      rightValue,
      leftIsWinner
    });

    if (!isCorrect) {
      setGameOver(true);
      saveRecordIfNeeded(mode, score);
      setIsResolvingRound(false);
      return;
    }

    const newScore = score + 1;
    setScore(newScore);

    const sessionId = gameSessionRef.current;
    clearRoundAdvanceTimeout();

    roundAdvanceTimeoutRef.current = setTimeout(async () => {
      roundAdvanceTimeoutRef.current = null;

      if (sessionId !== gameSessionRef.current) return;

      const incumbentPokemon = carriedPokemonName
        ? (currentPair.left?.name === carriedPokemonName ? currentPair.left : currentPair.right)
        : null;

      const nextCarryPokemon = incumbentPokemon
        ? (incumbentPokemon.name === currentPair.left?.name ? currentPair.right : currentPair.left)
        : pickedPokemon;

      const challenger = pickRandomPokemon([nextCarryPokemon?.name]);
      if (sessionId !== gameSessionRef.current) return;

      if (!challenger) {
        setGameOver(true);
        saveRecordIfNeeded(mode, newScore);
        setIsResolvingRound(false);
        return;
      }

      const isCarryOnLeft = currentPair.left?.name === nextCarryPokemon?.name;

      setCurrentPair({
        left: isCarryOnLeft ? nextCarryPokemon : challenger,
        right: isCarryOnLeft ? challenger : nextCarryPokemon
      });
      setCarriedPokemonName(nextCarryPokemon.name);
      setRoundFeedback(null);
      setCarryOverFeedback({
        pokemonName: nextCarryPokemon.name,
        value: getStatValue(nextCarryPokemon, mode)
      });
      setIsResolvingRound(false);
    }, 900);
  };

  useEffect(() => {
    return () => {
      gameSessionRef.current += 1;
      clearRoundAdvanceTimeout();
    };
  }, []);

  const currentModeLabel = STAT_MODES.find((item) => item.key === mode)?.label || 'Stat';

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <SEO
          title="Higher or Lower"
          description="Play Pokémon Higher or Lower. Guess which Pokémon form has the higher base stat and beat your best score in every mode."
          keywords="pokemon higher or lower, pokemon stats game, pokemon guessing game, pokemon base stats"
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress size={56} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <SEO
        title="Higher or Lower"
        description="Play Pokémon Higher or Lower. Guess which Pokémon form has the higher base stat and beat your best score in every mode: total, HP, Attack, Defense, Sp. Attack, Sp. Defense and Speed."
        keywords="pokemon higher or lower, pokemon stats game, pokemon guessing game, pokemon base stats, pokemon speed, pokemon attack"
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 230 }}>
          <InputLabel id="higher-lower-mode-label" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            Mode
          </InputLabel>
          <Select
            labelId="higher-lower-mode-label"
            value={mode}
            label="Mode"
            onChange={(event) => setMode(event.target.value)}
            aria-label="Select game mode"
            sx={{
              color: 'white',
              backgroundColor: 'rgba(30,30,30,0.9)',
              '& .MuiSvgIcon-root': { color: 'white' }
            }}
          >
            {STAT_MODES.map((statMode) => (
              <MenuItem key={statMode.key} value={statMode.key}>
                {statMode.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 0, sm: 1.25 }}
          sx={{ mr: 'auto', ml: { xs: 0, sm: 1 } }}
        >
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={includeLegendaries}
                onChange={(event) => setIncludeLegendaries(event.target.checked)}
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  '&.Mui-checked': { color: '#24CC9F' }
                }}
              />
            }
            label="Include legendaries"
            sx={{
              m: 0,
              '& .MuiFormControlLabel-label': {
                color: 'rgba(255,255,255,0.85)',
                fontSize: '0.9rem'
              }
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={onlyFinalEvolutions}
                onChange={(event) => setOnlyFinalEvolutions(event.target.checked)}
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  '&.Mui-checked': { color: '#24CC9F' }
                }}
              />
            }
            label="Final evolutions only"
            sx={{
              m: 0,
              '& .MuiFormControlLabel-label': {
                color: 'rgba(255,255,255,0.85)',
                fontSize: '0.9rem'
              }
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={includeMegas}
                onChange={(event) => setIncludeMegas(event.target.checked)}
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  '&.Mui-checked': { color: '#24CC9F' }
                }}
              />
            }
            label="Include Megas"
            sx={{
              m: 0,
              '& .MuiFormControlLabel-label': {
                color: 'rgba(255,255,255,0.85)',
                fontSize: '0.9rem'
              }
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={includeGigantamax}
                onChange={(event) => setIncludeGigantamax(event.target.checked)}
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  '&.Mui-checked': { color: '#24CC9F' }
                }}
              />
            }
            label="Include Gigantamax"
            sx={{
              m: 0,
              '& .MuiFormControlLabel-label': {
                color: 'rgba(255,255,255,0.85)',
                fontSize: '0.9rem'
              }
            }}
          />
        </Stack>

        <Stack direction="row" spacing={1}>
          <Tooltip title="View records" arrow>
            <IconButton
              onClick={() => setRecordsOpen(true)}
              aria-label="Open records"
              sx={{
                width: 44,
                height: 44,
                border: '1px solid rgba(255,255,255,0.2)',
                backgroundColor: 'rgba(30,30,30,0.95)',
                color: '#FFD54F'
              }}
            >
              <EmojiEventsIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Restart game" arrow>
            <IconButton
              onClick={() => {
                void resetGame();
              }}
              aria-label="Restart game"
              sx={{
                width: 44,
                height: 44,
                border: '1px solid rgba(255,255,255,0.2)',
                backgroundColor: 'rgba(30,30,30,0.95)',
                color: 'white'
              }}
            >
              <RestartAltIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Paper sx={{ p: 2.5, mb: 3, borderRadius: 3, backgroundColor: 'rgba(30,30,30,0.9)' }}>
        <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
          Higher or Lower
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.75)', mt: 1 }}>
          Guess which Pokémon/form has the highest <strong>{currentModeLabel}</strong> base stat.
          Changing mode restarts the run.
        </Typography>
        <Typography variant="h6" sx={{ color: '#24CC9F', mt: 1.5 }}>
          Current score: {score}
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error loading Pokémon data: {error}
        </Alert>
      )}

      {!error && pokemonPool.length < 2 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Not enough Pokémon data to start this game.
        </Alert>
      )}

      {!error && currentPair.left && currentPair.right && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
            gap: 2,
            alignItems: 'stretch'
          }}
        >
          <PokemonCandidateCard
            pokemon={currentPair.left}
            imageSrc={pairImageUrls.left}
            onImageError={(failedSrc) => handleCardImageError('left', failedSrc)}
            modeLabel={currentModeLabel}
            disabled={isResolvingRound || gameOver}
            feedback={
              roundFeedback
                ? {
                    value: roundFeedback.leftValue,
                    isPicked: roundFeedback.pickedSide === 'left',
                    outcome: roundFeedback.isTie
                      ? 'tie'
                      : roundFeedback.leftIsWinner
                        ? 'winner'
                        : 'loser'
                  }
                : carryOverFeedback && carryOverFeedback.pokemonName === currentPair.left?.name
                  ? {
                      value: carryOverFeedback.value,
                      isPicked: true,
                      outcome: 'memory'
                    }
                : null
            }
            onPick={() => handlePick('left')}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography
              variant="h4"
              sx={{
                color: '#24CC9F',
                fontWeight: 800,
                px: 2,
                py: 1,
                borderRadius: 2,
                border: '1px dashed rgba(36,204,159,0.6)'
              }}
            >
              VS
            </Typography>
          </Box>

          <PokemonCandidateCard
            pokemon={currentPair.right}
            imageSrc={pairImageUrls.right}
            onImageError={(failedSrc) => handleCardImageError('right', failedSrc)}
            modeLabel={currentModeLabel}
            disabled={isResolvingRound || gameOver}
            feedback={
              roundFeedback
                ? {
                    value: roundFeedback.rightValue,
                    isPicked: roundFeedback.pickedSide === 'right',
                    outcome: roundFeedback.isTie
                      ? 'tie'
                      : !roundFeedback.leftIsWinner
                        ? 'winner'
                        : 'loser'
                  }
                : carryOverFeedback && carryOverFeedback.pokemonName === currentPair.right?.name
                  ? {
                      value: carryOverFeedback.value,
                      isPicked: true,
                      outcome: 'memory'
                    }
                : null
            }
            onPick={() => handlePick('right')}
          />
        </Box>
      )}

      {gameOver && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Game over. Your final score in {currentModeLabel}: <strong>{score}</strong>. Best: <strong>{records[mode] || 0}</strong>.
          <Button
            onClick={() => {
              void resetGame();
            }}
            size="small"
            color="inherit"
            sx={{ ml: 1.2, fontWeight: 700, textTransform: 'none' }}
          >
            Play again
          </Button>
        </Alert>
      )}

      <Dialog
        open={recordsOpen}
        onClose={() => setRecordsOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperComponent={DraggablePaperComponent}
        aria-labelledby="higher-lower-records-title"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            background: 'linear-gradient(180deg, #13172A 0%, #0F1323 100%)',
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 16px 42px rgba(0, 0, 0, 0.45)'
          }
        }}
      >
        <DialogTitle
          id="higher-lower-records-title"
          sx={{
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            px: 2,
            py: 1.6,
            color: '#fff',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            background: 'linear-gradient(90deg, rgba(34,31,199,0.95), rgba(22,110,200,0.9))'
          }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <EmojiEventsIcon sx={{ color: '#FFD54F' }} />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Higher or Lower Records
            </Typography>
          </Stack>

          <IconButton
            onClick={() => setRecordsOpen(false)}
            size="small"
            aria-label="Close records"
            sx={{ color: 'rgba(255,255,255,0.9)' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2, pt: 2, pb: 2 }}>
          <Stack spacing={1.15} sx={{ mt: 1 }}>
            {STAT_MODES.map((statMode) => (
              <Paper
                key={statMode.key}
                sx={{
                  p: 1.35,
                  px: 1.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderRadius: 2,
                  background: 'linear-gradient(90deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))',
                  border: '1px solid rgba(255,255,255,0.12)'
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.92)' }}>
                  {statMode.label}
                </Typography>

                <Box
                  sx={{
                    minWidth: 58,
                    px: 1.2,
                    py: 0.45,
                    borderRadius: 999,
                    textAlign: 'center',
                    background: 'linear-gradient(90deg, rgba(36,204,159,0.22), rgba(36,204,159,0.4))',
                    border: '1px solid rgba(36,204,159,0.7)',
                    color: '#E7FFF7',
                    fontWeight: 900,
                    fontSize: '0.95rem',
                    letterSpacing: '0.2px',
                    textShadow: '0 1px 5px rgba(0,0,0,0.35)'
                  }}
                >
                  {records[statMode.key] ?? 0}
                </Box>
              </Paper>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default HigherOrLowerPage;
